import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { btnPrimary, inputCls, labelCls } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Download, DatabaseBackup, ShieldAlert, FileJson, FileSpreadsheet, RotateCcw,
  FileArchive, UploadCloud, Clock, CheckCircle2, XCircle,
} from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const EMPTY_SCHED = {
  enabled: false, frequency: "daily", hour_utc: 2, day_of_week: 1, day_of_month: 1,
  last_run: null, last_status: null, last_error: null, last_filename: null,
};

export default function DataBackup() {
  const { user } = useAuth();
  const canAccess = user?.role === "admin" || (user?.permissions || []).includes("data-admin");
  const [restoring, setRestoring] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [sched, setSched] = useState({ full_backup: EMPTY_SCHED, lead_documents: EMPTY_SCHED });
  const [schedSaving, setSchedSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");

  useEffect(() => {
    if (!canAccess) return;
    api.get("/settings/backup-schedule")
      .then((r) => setSched(r.data))
      .catch(() => {});
  }, [canAccess]);

  const download = async (url, fallbackName) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : fallbackName;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success("Download started");
    } catch (err) { toast.error("Download failed"); }
  };

  const uploadNow = async (kind) => {
    const url = kind === "full_backup" ? "/admin/backup/upload-ftp" : "/admin/documents-zip/upload-ftp";
    setUploadingKey(kind);
    try {
      const { data } = await api.post(url);
      toast.success(`Uploaded ${data.filename} to ${data.remote}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "FTP upload failed");
    }
    setUploadingKey("");
  };

  const saveSchedule = async () => {
    setSchedSaving(true);
    try {
      const payload = {
        full_backup: sliceSched(sched.full_backup),
        lead_documents: sliceSched(sched.lead_documents),
      };
      const { data } = await api.put("/settings/backup-schedule", payload);
      setSched(data);
      toast.success("Schedule saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSchedSaving(false);
  };

  const doRestore = async () => {
    if (!restoreFile) { toast.error("Choose a backup file first"); return; }
    if (confirmText !== "RESTORE") { toast.error('Type RESTORE to confirm'); return; }
    setRestoring(true);
    try {
      const text = await restoreFile.text();
      const res = await api.post("/admin/restore", text, { headers: { "Content-Type": "application/json" } });
      const n = Object.keys(res.data.restored || {}).length;
      toast.success(`Restore complete — ${n} collection(s) replaced`);
      setRestoreFile(null); setConfirmText("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Restore failed"); }
    setRestoring(false);
  };

  if (!canAccess) return <div className="text-slate-500" data-testid="data-backup-no-access">You don't have access to this page.</div>;

  return (
    <div className="space-y-6" data-testid="data-backup-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Data &amp; Backup</h1>
        <p className="text-sm text-slate-500 mt-1">Full database exports, document archives, scheduled FTP backups and disaster-recovery restore.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BackupCard
          icon={<DatabaseBackup size={18} className="text-blue-600" />}
          title="Full Backup"
          testId="backup"
          downloadUrl="/admin/backup"
          downloadFallback="arjun-backup.json"
          downloadLabel="Download Backup"
          uploadKind="full_backup"
          description="Complete snapshot (including logins) you can store safely and use to restore later."
          sched={sched.full_backup}
          setSched={(patch) => setSched((s) => ({ ...s, full_backup: { ...s.full_backup, ...patch } }))}
          download={download}
          uploadNow={uploadNow}
          uploadingKey={uploadingKey}
        />

        <BackupCard
          icon={<FileArchive size={18} className="text-amber-600" />}
          title="Lead Documents Backup"
          testId="lead-documents"
          downloadUrl="/admin/documents-zip"
          downloadFallback="arjun-lead-documents.zip"
          downloadLabel="Download ZIP"
          uploadKind="lead_documents"
          description={
            <>Every lead attachment packaged as <span className="font-mono text-slate-700">&lt;LEAD_NO&gt;_&lt;ATTACHMENT_NAME&gt;_XX.&lt;ext&gt;</span> in one ZIP.</>
          }
          sched={sched.lead_documents}
          setSched={(patch) => setSched((s) => ({ ...s, lead_documents: { ...s.lead_documents, ...patch } }))}
          download={download}
          uploadNow={uploadNow}
          uploadingKey={uploadingKey}
        />
      </div>

      <div className="flex items-center justify-between gap-3 bg-emerald-50/60 border border-emerald-200 rounded-xl px-4 py-3">
        <div className="text-sm text-emerald-800">
          Scheduled runs upload to the <b>FTP Server</b> configured in <a className="underline" href="/dashboard/ftp-config">Settings → FTP Server</a>. Times are in <b>IST (Asia/Kolkata)</b>.
        </div>
        <button data-testid="save-schedule-button" onClick={saveSchedule} disabled={schedSaving} className={btnPrimary}>
          <Clock size={16} /> {schedSaving ? "Saving…" : "Save Schedule"}
        </button>
      </div>

      <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-slate-900 font-semibold"><Download size={18} className="text-emerald-600" /> Full Database Export (reporting)</div>
        <p className="text-sm text-slate-500">Download a complete dump of every record for reporting/analysis. Passwords are excluded.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button data-testid="dump-json-button" onClick={() => download("/admin/db-dump?format=json", "arjun-dump.json")} className={btnPrimary}><FileJson size={16} /> Export JSON</button>
          <button data-testid="dump-csv-button" onClick={() => download("/admin/db-dump?format=csv", "arjun-dump.zip")} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm flex items-center gap-2"><FileSpreadsheet size={16} /> Export CSV (zip)</button>
        </div>
      </div>

      <div className="p-5 rounded-xl bg-white border border-rose-200 shadow-sm space-y-4">
        <div className="flex items-center gap-2 text-rose-700 font-semibold"><ShieldAlert size={18} /> Restore (Disaster Recovery)</div>
        <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <ShieldAlert size={16} className="mt-0.5 shrink-0" />
          <span><strong>Full replace:</strong> this wipes current data and replaces it with the backup. A pre-restore snapshot is saved on the server automatically. This cannot be undone from the UI.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Backup file (.json)</label>
            <input data-testid="restore-file-input" type="file" accept="application/json,.json" onChange={(e) => setRestoreFile(e.target.files[0] || null)} className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium hover:file:bg-slate-200" />
            {restoreFile && <div className="text-xs text-slate-500 mt-1">{restoreFile.name}</div>}
          </div>
          <div>
            <label className={labelCls}>Type <span className="font-mono text-rose-600">RESTORE</span> to confirm</label>
            <input data-testid="restore-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className={inputCls} placeholder="RESTORE" />
          </div>
        </div>
        <div className="flex justify-end">
          <button data-testid="restore-button" onClick={doRestore} disabled={restoring}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white font-medium text-sm flex items-center gap-2">
            <RotateCcw size={16} /> {restoring ? "Restoring…" : "Restore Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function sliceSched(s) {
  return {
    enabled: !!s.enabled,
    frequency: s.frequency || "daily",
    hour_utc: Math.max(0, Math.min(23, Number(s.hour_utc) || 0)),
    day_of_week: Math.max(1, Math.min(7, Number(s.day_of_week) || 1)),
    day_of_month: Math.max(1, Math.min(28, Number(s.day_of_month) || 1)),
  };
}

function BackupCard({ icon, title, testId, downloadUrl, downloadFallback, downloadLabel, uploadKind, description, sched, setSched, download, uploadNow, uploadingKey }) {
  return (
    <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm space-y-4" data-testid={`backup-card-${testId}`}>
      <div className="flex items-center gap-2 text-slate-900 font-semibold">{icon} {title}</div>
      <p className="text-sm text-slate-500">{description}</p>

      <div className="flex flex-wrap gap-2">
        <button data-testid={`${testId}-download-button`} onClick={() => download(downloadUrl, downloadFallback)} className={btnPrimary}>
          <Download size={16} /> {downloadLabel}
        </button>
        <button data-testid={`${testId}-upload-ftp-button`} onClick={() => uploadNow(uploadKind)} disabled={uploadingKey === uploadKind}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm flex items-center gap-2 disabled:opacity-60">
          <UploadCloud size={16} /> {uploadingKey === uploadKind ? "Uploading…" : "Upload to FTP now"}
        </button>
      </div>

      <div className="border-t border-slate-100 pt-3 space-y-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <input type="checkbox" checked={!!sched.enabled} onChange={(e) => setSched({ enabled: e.target.checked })}
                 className="w-4 h-4 accent-emerald-600" data-testid={`${testId}-schedule-enabled`} />
          <Clock size={14} /> Schedule to FTP
        </label>

        {sched.enabled && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>Frequency</label>
              <select value={sched.frequency} onChange={(e) => setSched({ frequency: e.target.value })} className={inputCls} data-testid={`${testId}-schedule-frequency`}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Hour (IST)</label>
              <input type="number" min="0" max="23" value={sched.hour_utc} onChange={(e) => setSched({ hour_utc: e.target.value })} className={inputCls} data-testid={`${testId}-schedule-hour`} />
            </div>
            {sched.frequency === "weekly" && (
              <div>
                <label className={labelCls}>Day of week</label>
                <select value={sched.day_of_week} onChange={(e) => setSched({ day_of_week: Number(e.target.value) })} className={inputCls}>
                  {DAYS.map((d, i) => <option key={d} value={i + 1}>{d}</option>)}
                </select>
              </div>
            )}
            {sched.frequency === "monthly" && (
              <div>
                <label className={labelCls}>Day of month (1-28)</label>
                <input type="number" min="1" max="28" value={sched.day_of_month} onChange={(e) => setSched({ day_of_month: Number(e.target.value) })} className={inputCls} />
              </div>
            )}
          </div>
        )}

        {sched.last_run && (
          <div className={`text-xs flex items-center gap-1 ${sched.last_status === "success" ? "text-emerald-700" : "text-rose-700"}`}>
            {sched.last_status === "success" ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            Last run {new Date(sched.last_run).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST — {sched.last_status}
            {sched.last_filename ? ` · ${sched.last_filename}` : ""}
            {sched.last_error ? ` · ${sched.last_error}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}
